import { useQuery } from "@tanstack/react-query";
import { DashboardEditContextValue } from "DashboardEditContext";
import { Day } from "date-fns";
import { de, enUS, Locale } from "date-fns/locale";
import _chunk from "lodash/chunk";
import { addParamToUrl, getRecordIdFromSearchParams, removeParamFromUrl } from "misc/urlHelpers";
import { ArchivableResourceType } from "molecules/ArchiveToggleDialogButton";
import CustomFieldInput from "molecules/CustomField/CustomFieldInput";
import { CloseDialogType } from "organisms/ButtonOrMenuItemWithDialog";
import { CustomFieldColumn } from "organisms/CustomFieldDataTableColumns";
import { FileDatagridContext } from "organisms/FileDatagridContext";
import { DropzoneHookPropsType } from "organisms/FileUploader/FileUploader.types";
import { GenerateDocumentProgressType } from "organisms/GenerateDocumentDialogButton/GenerateDocumentDialogButton.types";
import { GeneratedDocumentRecordType } from "pages/DocumentTemplate/DocumentTemplate.types";
import { EventRecordType } from "pages/Event/Event.types";
import { ALL_SETTINGS_KEYS } from "pages/Setting/SettingPage";
import { PublicSettingsContext } from "PublicDataContext";
import {
  ChangeEvent,
  ReactElement,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DateInput,
  FilterPayload,
  GetListParams,
  HttpError,
  Identifier,
  RaRecord,
  SortPayload,
  useAuthState,
  useDataProvider,
  useGetList,
  useGetOne,
  useListContext,
  useLocaleState,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
  useUnselectAll,
} from "react-admin";
import { useDropzone } from "react-dropzone";
import { UseFormSetError, useWatch } from "react-hook-form";
import { matchPath, useLocation, useSearchParams } from "react-router-dom";

import { InputChoicesType, SettingType } from "../App.types";
import { InputChoicesContext, StartUpSettingsContext } from "../BackendDataContext";
import { ContactInfoAbleType, ContactInfoTypesMapType } from "../organisms/ContactInfoGrid/ContactInfoGrid.types";
import { RecordHistoryRecordType } from "../organisms/RecordHistory/RecordHistory.types";
import { InvoiceRecordType } from "../pages/Booking/Booking.types";
import {
  CustomFieldContentTypeRecordType,
  CustomFieldRecordType,
  CustomFieldSectionWithFieldsRecordType,
} from "../pages/CustomField/CustomField.types";
import { EventBaseRecordType } from "../pages/EventBase/EventBase.types";
import {
  GenerateDocumentTaskRecordType,
  NoteRecordType,
  TodoListRecordType,
  UserRecordType,
  UserSetting,
  UserSettingKey,
  UserSettingValue,
} from "../pages/SharedRecords.types";
import { ParentRecordHistoryContext, RecordHistoryContext } from "../RecordHistoryContextProvider";
import {
  contactInfoDataIsPresent,
  convertFileToBase64,
  duplicateDates,
  duplicatePrices,
  duplicateResponsibilities,
  duplicateTodoLists,
  getRelatedResourceForFrontendResource,
} from "./dataHelpers";
import dataProvider, { ID_IN_BATCH_SIZE } from "./dataProvider";
import { ExtendedDataProvider } from "./dataProvider.types";
import { ValidationMissingValueObjectType } from "./draftValidationHelpers";
import { defaultListFetchParams, fetchHistoricalChanges, uploadFile } from "./edmClient";
import { ExportFieldsets } from "./exportFieldsets";
import { preprocessTimelineData } from "./historyDataHelpers";
import { BackendPermissionsType, PermissionsType, PermissionValues } from "./permission.types";
import { hasPermissionGroup } from "./permissionHelpers";
import { renderFullName } from "./renderHelpers";
import { delay, extractValidationMessages } from "./utils";

/* https://overreacted.io/making-setinterval-declarative-with-react-hooks/ */
export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef<(() => void) | undefined>(undefined);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useEscKey(callback: () => void) {
  const escFunction = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      callback();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);

    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, []);
}

export function useUrlId(
  idParamName: string,
  idAddedCallback: (id: number) => void,
): [(id: number) => void, () => void] {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = getRecordIdFromSearchParams(searchParams, idParamName) as number;

  useEffect(() => {
    if (id) idAddedCallback(id);
  }, [id]);

  const addIdToUrl = (id: number) => addParamToUrl(location, setSearchParams, idParamName, id);
  const removeIdFromUrl = () => removeParamFromUrl(location, setSearchParams, idParamName);

  return [addIdToUrl, removeIdFromUrl];
}

export function useExportFieldsets(initialFieldsets: ExportFieldsets | null) {
  const [fieldsets, setFieldsets] = useState(initialFieldsets);
  const [selection, setSelection] = useState<{ fields: string[]; translations: string[] }>({
    fields: [],
    translations: [],
  });

  function handleClickFieldset(name: string) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      if (!fieldsets) return;
      const fieldset = { ...fieldsets[name] };
      const newFields = { ...fieldset.fields };
      const { checked: isChecked } = e.target;

      Object.keys(newFields).forEach((fieldName) => {
        const field = newFields[fieldName];
        if (!field) return;
        field.isChecked = isChecked;
      });

      const newFieldsets = {
        ...fieldsets,
        [name]: {
          ...fieldset,
          isChecked,
          fields: newFields,
        },
      };

      setFieldsets(newFieldsets);
      calculateSelection(newFieldsets);
    };
  }

  function handleClickField(fieldName: string, fieldsetName: string) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      if (!fieldsets) return;
      const { checked: isChecked } = e.target;
      let fieldset = fieldsets[fieldsetName];
      if (!fieldset) return;
      fieldset = { ...fieldset };

      const field = fieldset.fields[fieldName];

      if (!field) return;
      field.isChecked = isChecked;

      const newFieldsets = { ...fieldsets, [fieldsetName]: fieldset };
      setFieldsets(newFieldsets);
      calculateSelection(newFieldsets);
    };
  }

  function calculateSelection(currentFieldsets: ExportFieldsets) {
    let selectedFields: string[] = [];
    let selectedFieldsTranslated: string[] = [];

    Object.keys(currentFieldsets).forEach((fieldsetName) => {
      const fieldset = currentFieldsets[fieldsetName];
      if (!fieldset) return;
      const fields = fieldset.fields;

      Object.keys(fields).forEach((key) => {
        const field = fields[key];
        if (!field) return;
        if (field.isChecked) {
          selectedFields = selectedFields.concat(key);
          let fieldName = field.name;
          if (fieldset.headersPrefix) {
            fieldName = `${fieldset.headersPrefix} - ${fieldName}`;
          }
          selectedFieldsTranslated = selectedFieldsTranslated.concat(fieldName);
        }
      });
    });

    return setSelection({ fields: selectedFields, translations: selectedFieldsTranslated });
  }

  useEffect(() => {
    if (initialFieldsets !== null) {
      setFieldsets(initialFieldsets);
      calculateSelection(initialFieldsets);
    }
  }, [JSON.stringify(initialFieldsets)]);

  return { fieldsets, selection, handleClickFieldset, handleClickField };
}

export function useCustomFieldContentType(
  contentType:
    | "event"
    | "eventbase"
    | "attendee"
    | "lecturer"
    | "booking"
    | "eventlocation"
    | "organization"
    | "organizationuser"
    | "user"
    | "order"
    | null,
) {
  const handleError = useErrorHandler();

  // useGetOne so every caller of the same content type shares one cached request
  const { data: customFieldContentType, isLoading } = useGetOne<CustomFieldContentTypeRecordType>(
    "custom_field_content_types",
    { id: contentType ?? "" },
    { enabled: !!contentType, onError: handleError },
  );

  const sections: CustomFieldSectionWithFieldsRecordType[] = useMemo(() => {
    if (!customFieldContentType) return [];

    const { custom_fields, custom_field_sections } = customFieldContentType;

    const fieldsBySectionId = new Map<number, CustomFieldRecordType[]>();
    for (const field of custom_fields) {
      if (!fieldsBySectionId.has(field.section)) {
        fieldsBySectionId.set(field.section, []);
      }
      fieldsBySectionId.get(field.section)!.push(field);
    }

    return custom_field_sections.map((section) => ({
      ...section,
      fields: fieldsBySectionId.get(section.id as number) || [],
    }));
  }, [customFieldContentType]);

  return {
    customFieldContentType,
    sections,
    isLoading,
  };
}

export function useValidationMissingValueRenderer() {
  const translate = useTranslate();
  return (
    value: string | ValidationMissingValueObjectType,
    translatePrefix?: string,
    translateOptions?: { smart_count?: number },
  ) => {
    if (typeof value === "string") {
      const translateKey = translatePrefix ? `${translatePrefix}.${value}` : value;
      return translate(translateKey, translateOptions || {});
    }
    return value.label;
  };
}

export function useGetManyAndRefetch<RecordType extends RaRecord>(
  resource: string,
  ids: Identifier[],
  shouldFetch?: boolean,
  refetchCondition?: (string | boolean | number)[],
) {
  const finallyShouldFetch = shouldFetch || true;

  const dataProvider = useDataProvider();
  const handleError = useErrorHandler();
  const [records, setRecords] = useState<RecordType[]>([]);

  const stringifiedIds = JSON.stringify(ids);
  const fetchAndSetRecords = useCallback(async () => {
    const ids = JSON.parse(stringifiedIds) as Identifier[];
    if (ids.length === 0 || !finallyShouldFetch) return setRecords([]);

    try {
      const res = await dataProvider.getMany<RecordType>(resource, { ids });
      setRecords(res.data || []);
    } catch (error) {
      handleError(error as Error);
    }
  }, [stringifiedIds]);

  useEffect(
    () => {
      fetchAndSetRecords();
    },
    refetchCondition || [stringifiedIds],
  );

  return records;
}

export function useGetListAndRefetch<RecordType extends RaRecord>(
  resource: string,
  filter: FilterPayload | null,
  updateCondition: string[] | null,
  shouldFetch: boolean = true,
  sort?: SortPayload,
) {
  const handleError = useErrorHandler();
  const [records, setRecords] = useState<RecordType[]>([]);

  const fetchAndSetRecords = useCallback(async () => {
    if (!shouldFetch) {
      setRecords([]);
      return;
    }

    const params = { ...defaultListFetchParams, filter };
    if (sort) {
      params.sort = sort;
    }

    try {
      const res = await dataProvider.getList<RecordType>(resource, params);
      setRecords(res.data || []);
    } catch (error) {
      setRecords([]);
      handleError(error as Error);
    }
  }, [resource, JSON.stringify(filter), JSON.stringify(sort), ...(updateCondition || [])]);

  useEffect(() => {
    fetchAndSetRecords();
  }, [fetchAndSetRecords]);

  return { records, refetch: fetchAndSetRecords };
}

type UseNoteOptions = {
  highPriority?: boolean;
  withEventRelatedNotes?: boolean;
  shouldFetch?: boolean;
};
type UseNotesType = (
  resource: string,
  relatedIds: Identifier[],
  opts: UseNoteOptions,
) => { notes: NoteRecordType[]; refetchNotes: () => void; isLoading: boolean };

export const useNotes: UseNotesType = (resource, relatedIds, opts = {}) => {
  const { highPriority, withEventRelatedNotes, shouldFetch = true } = opts;
  const dataProvider = useDataProvider();
  const relatedResource = getRelatedResourceForFrontendResource(resource);

  const filter = {
    related_id: relatedIds,
    related_resource: relatedResource,
    with_event_related_notes: withEventRelatedNotes,
    high_priority: highPriority,
  };
  const params = {
    ...defaultListFetchParams,
    filter,
    sort: { field: "created_at", order: "DESC" } as SortPayload,
  };

  const queryKey = [resource, "notes", { ids: relatedIds }];
  const queryFn = async () => {
    const response = await dataProvider.getList<NoteRecordType>("notes", params);
    return response;
  };

  const {
    data: result,
    isLoading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn,
    enabled: shouldFetch && relatedIds.length > 0,
  });

  const notes: NoteRecordType[] = result?.data || [];

  return { notes, refetchNotes: refetch, isLoading };
};
export function useRecordHistory(resource: string, id: Identifier) {
  const [data, setData] = useState<RecordHistoryRecordType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAndSetHistory = async () => {
    if (!id) return;

    setIsLoading(true);

    try {
      const data = await fetchHistoricalChanges(resource, id as number);
      const preprocessedData = preprocessTimelineData(data, resource);
      setData(preprocessedData);
    } catch {
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetHistory();
  }, [id, resource]);

  return { data, isLoading, refetchHistory: fetchAndSetHistory };
}

export function useInputChoicesContext(key: keyof InputChoicesType) {
  const context = useContext(InputChoicesContext);
  return context[key] || [];
}

export function useSettingsContext(key: string) {
  const context = useContext(StartUpSettingsContext);
  return context[key] || undefined;
}

export function usePublicSettingsContext(key: string) {
  const context = useContext(PublicSettingsContext);
  return context[key] || undefined;
}

export function useDashboardEditContext() {
  return useContext(DashboardEditContextValue);
}

export function useRecordHistoryContext() {
  return useContext(RecordHistoryContext);
}

export function useParentRecordHistoryContext() {
  return useContext(ParentRecordHistoryContext);
}

export function useFileDatagridContext() {
  return useContext(FileDatagridContext);
}

// storeKey must match the one given to ListBase, since react-admin keeps the selection
// under `${storeKey || resource}.selectedIds`.
export function useUnselectAllOnFirstRender(
  resource: string,
  updateCondition?: (string | number)[],
  storeKey?: string,
) {
  const unselectAll = useUnselectAll(resource, storeKey);

  useEffect(() => {
    unselectAll();
  }, updateCondition || []);
}

export function useToggleArchive(
  kind: "archive" | "unarchive",
  resource: ArchivableResourceType,
  ids?: Identifier[],
  callback?: () => void,
) {
  const finalIds = ids || [];
  const notify = useNotify();
  const translate = useTranslate();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<ExtendedDataProvider>();
  const unselectAll = useUnselectAll(resource);

  const toggleArchive = async () => {
    try {
      await Promise.all(finalIds.map((id) => dataProvider[kind](resource, id)));

      const translatedResource = translate(`resources.${resource}.name`, {
        smart_count: finalIds.length,
      });
      const messageArgs = { resource: translatedResource };
      notify(`archiving.${kind}_success`, { type: "info", messageArgs });
      unselectAll();
    } catch {
      notify(`archiving.${kind}_error`, { type: "error" });
    } finally {
      refresh();
      callback?.();
    }
  };

  return toggleArchive;
}

type UseRoleMutationOptions = {
  notification: "created" | "deleted";
  closeDialog: CloseDialogType;
  refetchHistory?: () => void;
};

// Orchestrates a role/login mutation dialog
export function useRoleMutation({ notification, closeDialog, refetchHistory }: UseRoleMutationOptions) {
  const dataProvider = useDataProvider<ExtendedDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleError = useErrorHandler();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const run = async (mutate: (dataProvider: ExtendedDataProvider) => Promise<unknown>) => {
    setIsSubmitting(true);
    try {
      await mutate(dataProvider);
      notify(`ra.notification.${notification}`, { type: "info", messageArgs: { smart_count: 1 } });
      refetchHistory?.();
      refresh();
      closeDialog();
    } catch (error) {
      handleError(error as Error, false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, run };
}

export function useGetSelectedEventBase() {
  const dataProvider = useDataProvider();
  type EventBaseSelectFormState = { event_base: number; child_event_base: number };

  const formEventBaseId = useWatch<EventBaseSelectFormState>({ name: "event_base" });
  const formChildEventBaseId = useWatch<EventBaseSelectFormState>({ name: "child_event_base" });
  const [eventBase, setEventBase] = useState<EventBaseRecordType | null>(null);
  const [childEventBase, setChildEventBase] = useState<EventBaseRecordType | null>(null);

  const fetchEventBase = useCallback(
    async (id: number | undefined, set: (event_base: EventBaseRecordType | null) => void) => {
      if (!id) return;
      try {
        const res = await dataProvider.getOne<EventBaseRecordType>("event_bases", { id });
        set(res.data);
      } catch {
        set(null);
      }
    },
    [],
  );

  useEffect(() => {
    fetchEventBase(formEventBaseId, setEventBase);
  }, [formEventBaseId, fetchEventBase]);
  useEffect(() => {
    fetchEventBase(formChildEventBaseId, setChildEventBase);
  }, [formChildEventBaseId, fetchEventBase]);

  if (childEventBase && eventBase && eventBase.group_children.includes(childEventBase.id)) {
    return childEventBase;
  } else if (eventBase) {
    return eventBase;
  } else {
    return undefined;
  }
}

export function useEnrollUsersToMoodleCourses() {
  const dataProvider = useDataProvider();

  const enrollUser =
    (eventId: Identifier, moodleCoursIds: Identifier[], user_role: string) => async (user: UserRecordType) => {
      for (const moodleCourseId of moodleCoursIds) {
        if (!user.moodle_user) continue;
        const data = {
          event: eventId,
          user: user.moodle_user.id,
          course: moodleCourseId,
          user_role: user_role,
        };
        await dataProvider.create("moodle_enrollments", { data });
      }
    };

  return async (users: UserRecordType[], moodleCourseIds: Identifier[], eventId: Identifier, user_role: string) =>
    await Promise.all(users.map(enrollUser(eventId, moodleCourseIds, user_role)));
}

export function useGetMaybeOne<RecordType extends RaRecord = RaRecord>(resource: string, props: { id?: Identifier }) {
  const [record, setRecord] = useState<RecordType | undefined>(undefined);
  const dataProvider = useDataProvider();

  useEffect(() => {
    if (!props.id) return setRecord(undefined);
    const fetchAndSetRecord = async () => {
      if (props.id) {
        try {
          const res = await dataProvider.getOne<RecordType>(resource, { id: props.id });
          setRecord(res.data);
        } catch {
          setRecord(undefined);
        }
      }
    };
    fetchAndSetRecord();
  }, [props.id]);

  return { data: record };
}

export function useContactInfos(record: ContactInfoAbleType) {
  const [contactInfoTypes, setContactInfoTypes] = useState<ContactInfoTypesMapType>({
    main_contact_info: contactInfoDataIsPresent(record, "main_contact_info"),
    invoice_contact_info: contactInfoDataIsPresent(record, "invoice_contact_info"),
    invoice_dispatch_contact_info: contactInfoDataIsPresent(record, "invoice_dispatch_contact_info"),
  });
  const allContactInfosPresent = () => Object.values(contactInfoTypes).every((value) => value);
  const someContactInfosPresent = () => Object.values(contactInfoTypes).some((value) => value);

  const refreshContactInfoTypes = (record: ContactInfoAbleType) => {
    setContactInfoTypes({
      main_contact_info: contactInfoDataIsPresent(record, "main_contact_info"),
      invoice_contact_info: contactInfoDataIsPresent(record, "invoice_contact_info"),
      invoice_dispatch_contact_info: contactInfoDataIsPresent(record, "invoice_dispatch_contact_info"),
    });
  };

  return {
    contactInfoTypes,
    setContactInfoTypes,
    allContactInfosPresent,
    someContactInfosPresent,
    refreshContactInfoTypes,
  };
}

export function useCustomFieldsFilters(resource: string, filters: ReactElement[] = []) {
  const translate = useTranslate();
  const { data: customFieldContentType } = useGetOne<CustomFieldContentTypeRecordType>("custom_field_content_types", {
    id: resource,
  });

  if (customFieldContentType) {
    customFieldContentType.custom_fields.forEach((f: CustomFieldRecordType) => {
      const source = `custom_fields_data__${f.slug}`;

      // dates are filtered as a range instead of a single value
      if (f.data_type === "DATE") {
        filters.push(
          <DateInput
            key={`${source}__gte`}
            source={`${source}__gte`}
            label={translate("filters.custom_field_date_from", { label: f.label })}
            variant="outlined"
          />,
          <DateInput
            key={`${source}__lte`}
            source={`${source}__lte`}
            label={translate("filters.custom_field_date_to", { label: f.label })}
            variant="outlined"
          />,
        );

        return;
      }

      let defaultValue;

      if (f.data_type === "BOOLEAN") {
        defaultValue = false;
      }

      filters.push(
        <CustomFieldInput field={f} label={f.label} source={source} defaultValue={defaultValue} hideLinks />,
      );
    });
  }

  return filters;
}

/**
 * Whether the current portal user owns any record of `resource`
 */
export function usePortalHasRecords(resource: string, filter: FilterPayload = {}) {
  const { authenticated } = useAuthState();
  const { permissions } = usePermissions<BackendPermissionsType>();
  const permissionsPending = !!authenticated && !permissions;
  const enabled = !!authenticated && !!permissions && !permissions.isStaff;

  const { total, isPending } = useGetList(resource, { pagination: { page: 1, perPage: 1 }, filter }, { enabled });

  return { hasRecords: (total ?? 0) > 0, isPending: permissionsPending || (enabled && isPending) };
}

/**
 * The current user's membership in `organizationId`, carrying the org permission
 * flags (`can_manage_users` and friends). Undefined for staff and for anyone who
 * does not belong to that organization.
 */
export function useOrganizationMembership(organizationId?: number) {
  const { permissions } = usePermissions<BackendPermissionsType>();

  return useMemo(() => {
    if (!organizationId || !permissions?.organizationMemberships) return undefined;
    return permissions.organizationMemberships.find((m) => m.organization_id === organizationId);
  }, [organizationId, permissions]);
}

export function useEdmPermissions(): PermissionsType {
  const { permissions, isLoading } = usePermissions<BackendPermissionsType>();
  const hasPermission = permissions ? hasPermissionGroup(permissions) : () => false;

  if (isLoading) {
    return {
      manageEvents: false,
      manageEventBases: false,
      manageOrders: false,
      manageAttendees: false,
      manageInterests: false,
      manageLecturers: false,
      manageTodosAndResponsibilities: false,
      manageBookings: false,
      manageInvoices: false,
      manageMoodle: false,
      manageSettings: false,
      manageTemplates: false,
      manageBaseData: false,
      manageAccess: false,
      manageUsers: false,
      viewAccounting: false,
      viewReporting: false,
      viewBookingList: false,
      viewInvoiceList: false,
      manageAccounting: false,
      manageEventFeedback: false,
      viewFeedbackReport: false,
      manageOrganizations: false,
    };
  } else {
    return {
      manageEvents: hasPermission("manage_events"),
      manageEventBases: hasPermission("manage_event_bases"),
      manageOrders: hasPermission("manage_orders"),
      manageAttendees: hasPermission("manage_attendees"),
      manageInterests: hasPermission("manage_interests"),
      manageLecturers: hasPermission("manage_lecturers"),
      manageTodosAndResponsibilities: hasPermission("manage_todos_and_responsibilities"),
      manageBookings: hasPermission("manage_bookings"),
      manageInvoices: hasPermission("manage_invoices"),
      manageMoodle: hasPermission("manage_moodle"),
      manageSettings: hasPermission("manage_settings"),
      manageTemplates: hasPermission("manage_templates"),
      manageBaseData: hasPermission("manage_base_data"),
      manageAccess: hasPermission("manage_access"),
      manageUsers: hasPermission("manage_users"),
      viewAccounting: hasPermission("view_accounting"),
      viewReporting: hasPermission("view_reporting"),
      viewBookingList: hasPermission("view_booking_list"),
      viewInvoiceList: hasPermission("view_invoice_list"),
      manageAccounting: hasPermission("manage_accounting"),
      manageEventFeedback: hasPermission("manage_event_feedback"),
      viewFeedbackReport: hasPermission("view_feedback_report"),
      manageOrganizations: hasPermission("manage_organizations"),
    };
  }
}

export function useCheckPermission() {
  const permissions = useEdmPermissions();

  return (neededPermissions?: PermissionValues[], checkType?: "some" | "every") => {
    const finalNeededPermissions = neededPermissions || [];
    if (finalNeededPermissions.length === 0) {
      return true;
    }

    if (checkType === "every") {
      return finalNeededPermissions.every((permission) => permissions[permission]);
    } else {
      return finalNeededPermissions.some((permission) => permissions[permission]);
    }
  };
}

/**
 * Hook that alerts clicks outside of the passed ref
 */
export function useOutsideClick(ref: RefObject<HTMLElement>, action: (event?: MouseEvent) => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        action(event);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, action]);
}

export function useDuplicateTodos() {
  const dataProvider = useDataProvider();

  const duplicateTodos = async (
    initialEvent: EventRecordType | Partial<EventRecordType>,
    newEvent: EventRecordType,
  ) => {
    if (newEvent && initialEvent) {
      const fetchParams: GetListParams = {
        ...defaultListFetchParams,
        sort: { field: "id", order: "ASC" as const },
        filter: { event: initialEvent.id },
      };
      const response = await dataProvider.getList<TodoListRecordType>("todo_lists", fetchParams);
      const todoLists = response.data;

      if (todoLists) {
        const duplicatedLists = duplicateTodoLists(todoLists, newEvent);

        await Promise.all(
          duplicatedLists.map(async (todoList) => {
            if (todoList.id) {
              await dataProvider.update("todo_lists", {
                id: todoList.id,
                data: todoList,
                previousData: {},
              });
            } else {
              await dataProvider.create("todo_lists", {
                data: todoList,
              });
            }
          }),
        );
      }
    }
  };

  return { duplicateTodos };
}

export function useEventCreate(onSuccess?: (event: EventRecordType) => void) {
  const dataProvider = useDataProvider();
  const { duplicateTodos } = useDuplicateTodos();
  const notify = useNotify();

  const createEvent = async (
    eventData: Record<string, unknown> = {},
    copyDataSwitches: Record<string, boolean>,
    selectedEventForCopy: Partial<EventRecordType> | EventRecordType | undefined,
  ) => {
    // remove possibly events prop from existing tab
    delete eventData["events"];

    // Check switches and set data
    if (selectedEventForCopy && selectedEventForCopy.id) {
      if (copyDataSwitches["prices"]) {
        eventData.prices = duplicatePrices(selectedEventForCopy.prices || []);
      }
      if (copyDataSwitches["event_dates"]) {
        eventData.dates = duplicateDates(selectedEventForCopy.dates || []);
      }
      if (copyDataSwitches["responsibilities"]) {
        eventData.responsibilities = duplicateResponsibilities(selectedEventForCopy.responsibilities || []);
      }
    }

    let result;

    try {
      result = await dataProvider.create<EventRecordType>("events", { data: eventData });

      if (selectedEventForCopy && selectedEventForCopy.id && copyDataSwitches["todos"]) {
        await duplicateTodos(selectedEventForCopy, result.data);
      }

      onSuccess?.(result.data);
    } catch {
      if (result?.data) {
        onSuccess?.(result.data);
        notify("notification.duplicate_todos_error", { type: "error" });
      } else {
        notify("ra.message.error", { type: "error" });
      }
    }
  };

  return { createEvent };
}

export function useErrorHandler() {
  const notify = useNotify();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (error: Error, withValidationErrors: boolean = true, setError?: UseFormSetError<any>) => {
    type ErrorValue = string | { serverError?: string };
    let validationErrors: { [key: string]: ErrorValue } | undefined;

    const errorBody = (error as HttpError).body as { errors: { [key: string]: ErrorValue } | undefined } | undefined;
    // A non-field error (``detail``) arrives as ``root: { serverError }``
    const messageOf = (value: ErrorValue) =>
      typeof value === "string" ? value : (value?.serverError ?? "ra.message.error");

    if (errorBody) {
      validationErrors = errorBody.errors;
    }

    // only show validation errors if they exist and are wanted,
    // otherwise these will get handled automatically by react admin
    // and we don't want to show any notifcation
    if (!!validationErrors && Object.keys(validationErrors).length > 0) {
      if (withValidationErrors) {
        const firstValidationErrorKey = Object.keys(validationErrors)[0];
        notify(messageOf(validationErrors[firstValidationErrorKey]), { type: "error" });

        if (setError) {
          Object.entries(validationErrors).forEach(([field, value]) => {
            setError(field, { type: "server", message: messageOf(value) });
          });
        }
      }
    } else if (error.message && Array.isArray(error.message)) {
      notify(error.message[0] as string, { type: "error" });
    } else if (error.message && typeof error.message === "string") {
      notify(error.message, { type: "error" });
    } else if (typeof error === "string") {
      notify(error, { type: "error" });
    } else {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  return handleError;
}

/**
 * Server validation errors captured for inline display instead of a snackbar. Long
 * messages (a capacity or registration-window violation names its event) are unreadable
 * as a toast and gone before staff can react, so callers render them next to the action
 * with ``ValidationErrorsAlert``. Anything that is not a field validation error still
 * goes to the default handler.
 */
export function useInlineValidationErrors() {
  const handleError = useErrorHandler();
  const [messages, setMessages] = useState<string[]>([]);

  const reset = useCallback(() => setMessages([]), []);

  const capture = useCallback((error: unknown) => {
    const extracted = extractValidationMessages(error);
    if (extracted.length > 0) {
      setMessages(extracted);
    } else {
      handleError(error as Error);
    }
  }, []);

  return { messages, capture, reset };
}

export function useBasePath() {
  const location = useLocation();
  const pathname = location.pathname;
  const createMatch = matchPath(":resource/create/*", pathname);
  const showMatch = matchPath(":resource/:id/show/*", pathname);
  const editMatch = matchPath(":resource/:id/*", pathname);
  const listMatch = matchPath(":resource/*", pathname);

  for (const match of [createMatch, showMatch, editMatch, listMatch]) {
    if (match) {
      return match.pathnameBase;
    }
  }

  return "";
}

export function usePollDocumentGenerationTaskProgress() {
  const [progress, setProgress] = useState<GenerateDocumentProgressType>({ total: 0, current: 0, otherPending: 0 });
  const [generatedDocuments, setGeneratedDocuments] = useState<GeneratedDocumentRecordType[]>([]);

  const initiateTaskProgressPolling = async (taskOrTaskId?: GenerateDocumentTaskRecordType | number | null) => {
    if (!taskOrTaskId) return;
    let task;

    if (typeof taskOrTaskId == "number") {
      const res = await dataProvider.generateDocumentTask(taskOrTaskId);
      task = res.data;
    } else {
      task = taskOrTaskId;
    }

    let state = "PENDING";
    let status = 200;
    const progressStep = Math.min(2, Math.max(1, Math.round(task.total_count / 10)));
    let timeoutCounter = 0;
    const timeout = 180;

    // Pull task status in ${progressStep} seconds intervals
    while (state === "PENDING" && status === 200) {
      setProgress({
        total: task.total_count,
        current: task.current_count || 0,
        otherPending: task.other_pending_documents_count || 0,
      });

      await delay(progressStep * 1000);
      timeoutCounter += progressStep;

      const res = await dataProvider.generateDocumentTask(task.id);
      state = res.data.state;
      status = res.status;
      task = res.data;

      setGeneratedDocuments(task.documents || []);

      if (timeoutCounter >= timeout) {
        break;
      }
    }

    setProgress({
      total: task.total_count,
      current: task.current_count || 0,
      error: task.state == "ERROR" ? task.state_message : null,
      otherPending: task.other_pending_documents_count || 0,
    });
  };

  return { initiateTaskProgressPolling, progress, generatedDocuments };
}

export function useFileUploadInput() {
  const [selectedFile, setSelectedFile] = useState<File | undefined>();

  const transform = useCallback(
    async (data: RaRecord) => {
      if (selectedFile) {
        const fileData = await convertFileToBase64(selectedFile);
        data.file = {
          data: fileData,
          original_filename: selectedFile.name,
          tags: [],
        };
      }
      return data;
    },
    [selectedFile],
  );

  return { selectedFile, setSelectedFile, transform };
}

export function useUserSetting<SettingValue extends UserSettingValue>(key: UserSettingKey, defaultValue: SettingValue) {
  const dataProvider = useDataProvider();
  const [settingValue, setSettingValue] = useState<SettingValue>(defaultValue);
  const [isFetching, setIsFetching] = useState(true);
  const notify = useNotify();

  const fetchSetting = async () => {
    setIsFetching(true);
    try {
      const res = await dataProvider.getList<UserSetting>("user_settings", { filter: { key } });
      const setting = res.data[0];
      if (setting) {
        const value = res.data[0].value as SettingValue;
        setSettingValue(value);
      }
    } catch (e) {
      const error = e as { status: number; message?: string };
      if (error.status !== 404) {
        notify(error.message || "ra.notification.http_error", {
          type: "error",
          messageArgs: { _: error.message },
        });
      }
    } finally {
      setIsFetching(false);
    }
  };

  const persistSetting = async (value: UserSettingValue) => {
    const res = await dataProvider.create<UserSetting>("user_settings", { data: { key, value } });
    const resValue = res.data.value as SettingValue;
    setSettingValue(resValue);
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  return { settingValue, setSettingValue, fetchSetting, isFetching, persistSetting };
}

export function useEdmFileUploadDropzone(props: DropzoneHookPropsType) {
  const {
    relatedResource,
    relatedId,
    tags = [],
    visibleForAll,
    refetchRelatedRecord,
    onUploadSuccess,
    resource,
    options = {},
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!relatedId) return;
      const uploadedFiles = acceptedFiles.map(async (file) => {
        try {
          const fileData = await convertFileToBase64(file);
          return await uploadFile({
            fileData,
            relatedResource,
            relatedId,
            originalFilename: file.name,
            tags,
            visibleForAll,
          });
        } catch {
          return Promise.reject(new Error("Error uploading file"));
        }
      });

      setIsLoading(true);

      try {
        const finishedUploadedFiles = await Promise.all(uploadedFiles);
        notify("notification.upload_success", { type: "info" });
        refetchRelatedRecord?.();
        onUploadSuccess?.(finishedUploadedFiles);
      } catch {
        notify("notification.upload_error", { type: "error" });
      }

      setIsLoading(false);
    },
    [relatedResource, relatedId, notify, resource, tags],
  );

  return { isLoading, ...useDropzone({ ...options, onDrop }) };
}

type ResolvedRelation = {
  id: number;
  label: string;
  resource: string;
};

export default function useResolveHistoryRelations() {
  const dataProvider = useDataProvider();
  const translate = useTranslate();

  const resolveRelation = useCallback(
    async (resource: string, id: number): Promise<ResolvedRelation | null> => {
      try {
        const { data } = await dataProvider.getOne<RaRecord>(resource, { id });

        const getLabelForResource = (): string => {
          if (["lecturers", "attendees", "staff_members"].includes(resource)) {
            return renderFullName(data, { reverse: false });
          } else if (resource === "events") {
            return (data as EventRecordType).id_number;
          } else if (data.name) {
            return data.name as string;
          }

          return `ID: ${id}`;
        };

        const resolved: ResolvedRelation = {
          id,
          label: getLabelForResource(),
          resource,
        };
        return resolved;
      } catch {
        return {
          id,
          label: translate("history.deleted_record", { id }),
          resource,
        };
      }
    },
    [dataProvider, translate],
  );

  const resolveRelationValue = useCallback(
    async (resource: string, value: number | number[]): Promise<string> => {
      if (value === null || value === undefined) {
        return "";
      }

      // Single relation
      if (typeof value === "number") {
        const resolved = await resolveRelation(resource, value);
        return resolved?.label || `ID: ${value}`;
      }

      // Multi relation
      if (Array.isArray(value)) {
        const resolved = await Promise.all(value.map((id) => resolveRelation(resource, id)));
        return resolved
          .filter((r): r is ResolvedRelation => r !== null)
          .map((r) => r.label)
          .join(", ");
      }

      return String(value);
    },
    [resolveRelation],
  );

  return { resolveRelationValue };
}

export function useMenuCountPolling(fetchFn: (() => Promise<number>) | null, interval?: number) {
  const [count, setCount] = useState(0);
  const finalInterval = interval || 300000; // 5 minutes;

  const fetchAndSetCount = async () => {
    if (!fetchFn) {
      setCount(0);
      return;
    }
    try {
      const count = await fetchFn();
      setCount(count);
    } catch {
      setCount(0);
    }
  };

  useEffect(() => {
    fetchAndSetCount();
  }, [fetchFn]);

  useInterval(fetchAndSetCount, fetchFn ? finalInterval : null);

  return count;
}

/**
 * The root invoices of one order; corrections / reversals come nested via
 * ``children``. One hook so every consumer on the order page hits the same query
 * key and the list is fetched once. ``perPage`` is raised well past the default 25
 * because callers derive facts from the whole set, not just a first page.
 */
export function useOrderRootInvoices(orderId: number) {
  return useGetList<InvoiceRecordType>("invoices", {
    filter: { order: orderId, parent__isnull: true },
    pagination: { page: 1, perPage: 500 },
  });
}

export function useCustomFieldColumns(resource: string) {
  const { data } = useGetOne<CustomFieldContentTypeRecordType>("custom_field_content_types", { id: resource });

  return useMemo(() => {
    if (!data) return { columns: [] as CustomFieldColumn[], sources: [] as string[] };

    const columns = data.custom_fields.map((f) => ({
      source: `custom_fields_data.${f.slug}`,
      label: f.label,
      customField: f,
    }));

    return { columns, sources: columns.map((c) => c.source) };
  }, [data]);
}

export type UserRoles = {
  isAttendee: boolean;
  isLecturer: boolean;
  isStaff: boolean;
  canManageAttendee: boolean;
  canManageLecturer: boolean;
  canManageStaff: boolean;
  canManageUser: boolean;
};

export function useUserRoles(user: UserRecordType | undefined): UserRoles {
  const { manageAttendees, manageLecturers, manageAccess, manageUsers } = useEdmPermissions();

  return {
    isAttendee: !!user?.is_attendee,
    isLecturer: !!user?.is_lecturer,
    isStaff: !!user?.is_staff,
    canManageAttendee: manageAttendees,
    canManageLecturer: manageLecturers,
    canManageStaff: manageAccess,
    canManageUser: manageUsers || manageAccess,
  };
}

type SettingsRecord = Record<string, SettingType>;

export function useSettingsData() {
  const dataProvider = useDataProvider<ExtendedDataProvider>();
  const notify = useNotify();
  const handleError = useErrorHandler();

  const [loading, setLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState<Record<string, unknown>>({});
  const settingsByKey = useRef<SettingsRecord>({});

  useEffect(() => {
    dataProvider
      .getList<SettingType>("settings", {
        filter: { only_default_settings: "True" },
        pagination: { page: 1, perPage: 200 },
        sort: { field: "key", order: "ASC" },
      })
      .then(({ data }) => {
        const vals: Record<string, unknown> = {};
        const byKey: SettingsRecord = {};
        data.forEach((s) => {
          if (ALL_SETTINGS_KEYS.includes(s.key)) {
            vals[s.key] = s.value;
            byKey[s.key] = s;
          }
        });
        setDefaultValues(vals);
        settingsByKey.current = byKey;
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = useCallback(
    async (keys: string[], formValues: Record<string, unknown>) => {
      const changed = keys.filter(
        (key) => key in settingsByKey.current && JSON.stringify(formValues[key]) !== JSON.stringify(defaultValues[key]),
      );

      if (changed.length === 0) return;
      try {
        await Promise.all(
          changed.map((key) =>
            dataProvider.update("settings", {
              id: settingsByKey.current[key].id,
              data: { value: formValues[key] ?? null },
              previousData: settingsByKey.current[key],
            }),
          ),
        );
        setDefaultValues((prev) => {
          const next = { ...prev };
          changed.forEach((key) => {
            next[key] = formValues[key];
          });
          return next;
        });
        notify("ra.notification.updated", { messageArgs: { smart_count: changed.length } });
      } catch (error) {
        handleError(error as Error);
      }
    },
    [defaultValues, dataProvider, notify, handleError],
  );

  return { loading, defaultValues, handleSave };
}

export function useDateFnsLocaleConfig(): { locale: Locale; weekStartsOn: Day } {
  const [locale] = useLocaleState();

  switch (locale) {
    case "de":
      return { locale: de, weekStartsOn: 1 };

    default:
      return { locale: enUS, weekStartsOn: 0 };
  }
}

/**
 * The selected records of the surrounding list. A selection can span pages (or come
 * from "select all"), so records missing from the loaded page are fetched by id. Empty
 * while that fetch runs - callers disable their actions on an empty list.
 */
export function useSelectedRecords<RecordType extends RaRecord>(): RecordType[] {
  const { data, selectedIds, resource } = useListContext<RecordType>();
  const dataProvider = useDataProvider();
  const loaded = (data ?? []).filter((record) => selectedIds.includes(record.id));
  const needsFetch = loaded.length < selectedIds.length;

  const { data: fetched } = useQuery({
    queryKey: [resource, "selectedRecords", selectedIds],
    queryFn: async () => {
      const batches = _chunk(selectedIds, ID_IN_BATCH_SIZE);
      const results = await Promise.all(
        batches.map((ids) =>
          dataProvider.getList<RecordType>(resource, {
            filter: { id__in: ids },
            pagination: { page: 1, perPage: ids.length },
            sort: { field: "id", order: "ASC" },
          }),
        ),
      );
      return results.flatMap((result) => result.data);
    },
    enabled: needsFetch,
  });

  return needsFetch ? (fetched ?? []) : loaded;
}
